import { AthleteTeamProfile, athleteDisplayName } from '@strength/domain';
import { createColumnHelper } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../trpc/react';
import { DataTableWithContext } from '../../components/table/data-table';
import { useViewer } from '../../context/viewer';
import { getLocale } from '../../utils/i18n';
import { AthleteProfile } from './athlete-profile';

export const ProfilesPage = () => {
  const { t } = useTranslation();
  const [selectedAthlete, setSelectedAthlete] =
    useState<AthleteTeamProfile | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const {
    team: { selectedTeam: teamId },
  } = useViewer();
  const teamAthleteResult = trpc.roster.teamAthletes.useQuery({}, {
    staleTime: 10_000,
  });

  // Apply filter to create filteredData
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return teamAthleteResult.data || [];
    }

    const searchTermLower = searchTerm.toLowerCase();
    return (
      teamAthleteResult.data?.filter((row) => {
        const fullName = athleteDisplayName(row).toLowerCase();
        return fullName.includes(searchTermLower);
      }) || []
    );
  }, [teamAthleteResult.data, searchTerm]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const columnHelper = createColumnHelper<AthleteTeamProfile>();

  const columns = [
    columnHelper.accessor('givenName', {
      header: () => t('profile'),
      cell: ({ row }) => athleteDisplayName(row.original),
    }),
    columnHelper.accessor('email', {
      header: () => t('email'),
      cell: ({ row }) => row.original.email,
    }),
    columnHelper.accessor('primaryPhone', {
      header: () => t('phoneNumber'),
      cell: ({ row }) => row.original.primaryPhone,
    }),
    columnHelper.accessor('birthdate', {
      header: () => t('dateOfBirth'),
      size: 200,
      cell: ({ row }) =>
        row.original.birthdate
          ? DateTime.fromISO(row.original.birthdate).toLocaleString(
              DateTime.DATE_SHORT,
              { locale: getLocale() },
            )
          : '',
    }),
    columnHelper.accessor('attributes.athleteYear', {
      header: () => t('schoolYear'),
      cell: ({ row }) => row.original.attributes?.athleteYear,
    }),
    columnHelper.accessor('attributes.positions', {
      header: () => t('positions'),
      cell: ({ row }) => row.original.attributes?.positions?.join(', '),
    }),
  ];

  return (
    <div className="p-6 grow overflow-hidden h-full flex flex-col">
      <DataTableWithContext
        title={t('profiles')}
        columns={columns}
        data={filteredData}
        loading={
          teamAthleteResult.status === 'loading' && !teamAthleteResult.data
        }
        enablePagination
        initialPageSize={25}
        pageSizeOptions={[25, 50, 100]}
        onRowClick={(row) => setSelectedAthlete(row)}
        enableSearch={true}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t('searchForProfile')}
      />
      {selectedAthlete && (
        <AthleteProfile
          open={true}
          onOpenChange={() => setSelectedAthlete(null)}
          athlete={selectedAthlete}
        />
      )}
    </div>
  );
};
